import { useMemo, useState } from "react";

import clsx from "clsx";
import { orderBy } from "lodash";

import RegularGraphic from "@assets/page-loading.gif";
import { DataTypeProvider } from "@devexpress/dx-react-grid";
import {
  Grid,
  TableColumnResizing,
  TableHeaderRow,
  VirtualTable,
} from "@devexpress/dx-react-grid-bootstrap4";
import "@devexpress/dx-react-grid-bootstrap4/dist/dx-react-grid-bootstrap4.css";
import { useProductAnalyticsContext } from "@features/product-analytics/hooks";
import { useNorthbeamQuery } from "@hooks/http";
import { useDesignElementManager } from "@hooks/use-design-element-manager";
import { makeAOV, makeCac, makeRoas } from "@north-beam/nb-client-side";
import { makeASP } from "@north-beam/nb-client-side/metrics";
import {
  type FetchProductAnalyticsProductsByPlatformAndCampaignVariables,
  type FetchProductAnalyticsProductsByPlatform,
  type FetchProductAnalyticsProductsByPlatformVariables,
  type FetchProductAnalyticsProductsByPlatformAndCampaign,
  type FetchProductAnalyticsProductsByPlatformCampaignAd,
  type FetchProductAnalyticsProductsByPlatformCampaignAdVariables,
} from "@north-beam/nb-client-side/nb-api-graphql-generated";
import { todayLocalAsISODate } from "@north-beam/nb-common";
import { PrimaryButton } from "@shared/buttons";
import { Modal } from "@shared/modal";
import { parseDateRangeArgument } from "@utils/index";

import {
  assignPercentages,
  filterDataByColumnAndOperation,
  rollUpMetricRows,
} from "../metricsData";
import {
  FETCH_PRODUCT_ANALYTICS_PRODUCTS_BY_PLATFORM,
  FETCH_PRODUCT_ANALYTICS_PRODUCTS_BY_PLATFORM_AND_CAMPAIGN,
  FETCH_PRODUCT_ANALYTICS_PRODUCTS_BY_PLATFORM_CAMPAIGN_AD,
} from "../queries";

import { TableCell, TableRow } from "./table/body-components";
import { NAME_COLUMN_ID, ROW_HEIGHT } from "./table/constants";
import {
  CacPercentage,
  Currency,
  EfficiencyIndex,
  Multiplier,
  NameDataFormatter,
  Decimal,
} from "./table/data-type-formatters";
import { ASPClickThrough } from "./table/data-type-formatters/asp-url-clickthrough";
import {
  TableHeaderContent,
  TableHeadWithFixedRow,
} from "./table/header-components";

interface ProductItemRelationModalProps {
  isModalOpen: boolean;
  setIsModalOpen: (open: boolean) => void;
  item: any;
}

const ProductItemRelationModal = ({
  isModalOpen,
  setIsModalOpen,
  item,
}: ProductItemRelationModalProps) => {
  const { tabState, filters, tableState, selectedRelationRow } =
    useProductAnalyticsContext();

  const { designElementManager } = useDesignElementManager();

  const [columns] = useState([
    { name: "name", title: "Product Name" },
    { name: "rev", title: "Revenue" },
    { name: "rev_first_time", title: "Rev (1st Time)" },
    { name: "rev_returning", title: "Rev (Returning)" },
    { name: "txns", title: "Orders" },
    { name: "quantity", title: "Units Sold" },
    { name: "cacPercentage", title: "CAC index" },
    { name: "roasPercentage", title: "Roas Index" },
    { name: "proportional_spend", title: "Product Spend" },
    { name: "proas", title: "Product ROAS" },
    { name: "pcac", title: "Product CAC" },
    { name: "aov", title: "AOV" },
    { name: "asp", title: "ASP" },
    { name: "itemSoldRatio", title: "Item Ratio" },
  ]);

  const [defaultColumnWidths] = useState([
    { columnName: "name", width: 300 },
    { columnName: "rev", width: 150 },
    { columnName: "rev_first_time", width: 150 },
    { columnName: "rev_returning", width: 150 },
    { columnName: "txns", width: 100 },
    { columnName: "quantity", width: 130 },
    { columnName: "cacPercentage", width: 120 },
    { columnName: "roasPercentage", width: 120 },
    { columnName: "proportional_spend", width: 150 },
    { columnName: "proas", width: 150 },
    { columnName: "pcac", width: 150 },
    { columnName: "aov", width: 120 },
    { columnName: "asp", width: 120 },
    { columnName: "itemSoldRatio", width: 120 },
  ]);

  const dateRange = parseDateRangeArgument(
    filters.dateRange,
    todayLocalAsISODate(),
  );

  const { data: byCampaignProducts, loading: byCampaignProductsLoading } =
    useNorthbeamQuery<
      FetchProductAnalyticsProductsByPlatformAndCampaign,
      FetchProductAnalyticsProductsByPlatformAndCampaignVariables
    >(FETCH_PRODUCT_ANALYTICS_PRODUCTS_BY_PLATFORM_AND_CAMPAIGN, {
      notifyOnNetworkStatusChange: true,
      skip:
        (!isModalOpen && !selectedRelationRow?.campaign) ||
        !!selectedRelationRow?.ad,
      variables: {
        filter: {
          dateRange: {
            start: dateRange.startDate,
            end: dateRange.endDate,
          },
          accountingMode: filters.accountingMode,
          attributionModel: filters.attributionModel,
          attributionWindow: filters.attributionWindow,
          platformIds: selectedRelationRow?.platforms || [],
          campaignIds: [selectedRelationRow?.campaign],
        },
      },
    });

  const { data: byAdProducts, loading: byAdProductsLoading } =
    useNorthbeamQuery<
      FetchProductAnalyticsProductsByPlatformCampaignAd,
      FetchProductAnalyticsProductsByPlatformCampaignAdVariables
    >(FETCH_PRODUCT_ANALYTICS_PRODUCTS_BY_PLATFORM_CAMPAIGN_AD, {
      notifyOnNetworkStatusChange: true,
      skip: !isModalOpen && !selectedRelationRow?.ad,
      variables: {
        filter: {
          dateRange: {
            start: dateRange.startDate,
            end: dateRange.endDate,
          },
          accountingMode: filters.accountingMode,
          attributionModel: filters.attributionModel,
          attributionWindow: filters.attributionWindow,
          platformIds: [selectedRelationRow?.platform],
          campaignIds: [selectedRelationRow?.campaign],
          adIds: [selectedRelationRow?.ad],
        },
      },
    });

  const { data: byPlatformProducts, loading: byPlatformProductsLoading } =
    useNorthbeamQuery<
      FetchProductAnalyticsProductsByPlatform,
      FetchProductAnalyticsProductsByPlatformVariables
    >(FETCH_PRODUCT_ANALYTICS_PRODUCTS_BY_PLATFORM, {
      notifyOnNetworkStatusChange: true,
      skip:
        !isModalOpen ||
        !!selectedRelationRow?.ad ||
        !!selectedRelationRow?.campaign,
      variables: {
        filter: {
          dateRange: {
            start: dateRange.startDate,
            end: dateRange.endDate,
          },
          accountingMode: filters.accountingMode,
          attributionModel: filters.attributionModel,
          attributionWindow: filters.attributionWindow,
          platformIds: selectedRelationRow?.platforms || [],
        },
      },
    });

  let data;

  if (selectedRelationRow?.ad) {
    data = byAdProducts?.me.ProductAnalyticsProductsByPlatformCampaignAd;
  } else if (selectedRelationRow?.campaign) {
    data = byCampaignProducts?.me.productAnalyticsProductsByPlatformAndCampaign;
  } else {
    data = byPlatformProducts?.me.productAnalyticsProductsByPlatform;
  }

  const cacMetric = makeCac(
    filters.attributionModel,
    filters.attributionWindow,
    "",
    "",
    filters.accountingMode,
  );
  const ROASMetric = makeRoas(
    filters.attributionModel,
    filters.attributionWindow,
    "",
    "",
    filters.accountingMode,
  );
  const AOVMetric = makeAOV(
    filters.attributionModel,
    filters.attributionWindow,
    "",
    "",
    filters.accountingMode,
  );

  const ASPMetric = makeASP(
    filters.attributionModel,
    filters.attributionWindow,
    "",
    "",
    filters.accountingMode,
  );

  let filteredRows = data?.summary.filter((product) => {
    return tableState.selectedProducts.includes(product?.product_id || "");
  });

  if ((filteredRows?.length || 0) === 0) {
    filteredRows = data?.summary;
  }

  if (
    tableState.metricFilters.length !== 0 &&
    (data?.summary?.length || 0) > 0
  ) {
    filteredRows = tableState.metricFilters.reduce(
      (inProgressFilteredRows, filter) => {
        return filterDataByColumnAndOperation(
          filter.value,
          inProgressFilteredRows || [],
          filter.operator,
          filter.id,
        );
      },
      filteredRows || [],
    );
  }

  const productsWithMetrics = useMemo(
    () =>
      assignPercentages(
        rollUpMetricRows({
          rows:
            filteredRows?.map((product) => {
              return {
                ...product,
                id: product?.product_id || "",
                rev: product?.rev || 0,
                spend: product?.spend || 0,
                quantity: product?.quantity || 0,
                name: product?.product_title_latest || "",
                rev_returning: product?.returning_total_revenue || 0,
                rev_first_time: product?.total_revenue_first_time || 0,
                proportional_spend: product?.proportional_spend || 0,
                order_rev: product.order_rev || 0,
                txns: product?.txns || 0,
              };
            }) || [],
          attributionModel: filters.attributionModel,
          attributionWindow: filters.attributionWindow,
          cacMetric,
          ROASMetric,
          AOVMetric,
          ASPMetric,
        }),
      ),
    [
      AOVMetric,
      ASPMetric,
      ROASMetric,
      cacMetric,
      filteredRows,
      filters.attributionModel,
      filters.attributionWindow,
    ],
  );

  const filteredAndSortedRows = orderBy(
    productsWithMetrics,
    [tableState.sorting.id],
    [tableState.sorting.order],
  ).map((row, index) => {
    return {
      ...row,
      index,
    };
  });

  return (
    <Modal
      isOpen={isModalOpen}
      onClose={() => {
        setIsModalOpen(false);
      }}
      hideCloseButton
      size="3xl"
    >
      {isModalOpen ? (
        <div>
          <div className="flex justify-between">
            <div>
              <div className="flex items-center  gap-2">
                <div className="text-v2-primary text-lg font-semibold flex items-center gap-2">
                  {tabState.tabView === "platform" &&
                    designElementManager
                      .getDesignElement(item.name)
                      .getComponent({ size: "26" })}
                  {item.name}
                </div>
              </div>
              <div className=" text-[#808997] text-[14px] font-normal">
                Products
              </div>
            </div>
            <i
              className={clsx(
                "fa-regular fa-sharp fa-times cursor-pointer",
                "text-primary text",
              )}
              onClick={() => setIsModalOpen(false)}
            />
          </div>
          {byPlatformProductsLoading ||
          byCampaignProductsLoading ||
          byAdProductsLoading ? (
            <div className="h-[600px]">
              <img
                src={RegularGraphic}
                alt="Loading screen gif"
                className={clsx("w-1/2 m-auto")}
              />
            </div>
          ) : (
            <>
              <div
                className={clsx(
                  "sales-table-v3 sales-table-v3-compact-row-height h-[38rem]",
                  "border border-gray bg-v2-primary rounded-lg overflow-hidden relative text-xs",
                )}
              >
                <Grid
                  rows={filteredAndSortedRows}
                  columns={columns}
                  getRowId={(row) => row.id}
                >
                  <VirtualTable
                    height="auto"
                    estimatedRowHeight={ROW_HEIGHT}
                    rowComponent={(props) => <TableRow {...props}></TableRow>}
                    headComponent={
                      TableHeadWithFixedRow as React.ComponentType<object>
                    }
                    cellComponent={TableCell}
                  />
                  <TableColumnResizing
                    defaultColumnWidths={defaultColumnWidths}
                  />
                  <TableHeaderRow contentComponent={TableHeaderContent} />
                  <DataTypeProvider
                    for={[NAME_COLUMN_ID]}
                    formatterComponent={NameDataFormatter}
                  />
                  <DataTypeProvider
                    for={["cacPercentage"]}
                    formatterComponent={CacPercentage}
                  />
                  <DataTypeProvider
                    for={["roasPercentage"]}
                    formatterComponent={EfficiencyIndex}
                  />
                  <DataTypeProvider
                    for={[
                      "rev",
                      "aov",
                      "pcac",
                      "proportional_spend",
                      "spend",
                      "rev_first_time",
                      "rev_returning",
                    ]}
                    formatterComponent={Currency}
                  />
                  <DataTypeProvider
                    for={["txns", "quantity"]}
                    formatterComponent={Decimal}
                  />
                  <DataTypeProvider
                    for={["asp"]}
                    formatterComponent={ASPClickThrough}
                  />
                  <DataTypeProvider
                    for={["proas", "itemSoldRatio"]}
                    formatterComponent={Multiplier}
                  />
                </Grid>
              </div>
              <div className="flex justify-end pt-6 w-full ">
                <PrimaryButton
                  onClick={() => {
                    setIsModalOpen(false);
                  }}
                  className="uppercase"
                >
                  Close
                </PrimaryButton>
              </div>
            </>
          )}
        </div>
      ) : (
        <div className="w-[100vw] h-[600px]"></div>
      )}
    </Modal>
  );
};

export default ProductItemRelationModal;
